/**
 * Campaign Context for React
 *
 * Provides React Context and hooks for dependency injection of campaign managers.
 * Allows components to access campaign managers without direct instantiation,
 * enabling better testability and separation of concerns.
 */

import React, { createContext, useContext, useMemo, ReactNode } from 'react';
import { CampaignStateManager } from './CampaignStateManager';
import { CampaignManagerFactory } from './CampaignManagerFactory';
import { CampaignConfig } from '../types/CampaignTypes';

/**
 * Context value interface for campaign manager factory.
 * Provides method to get or create campaign managers.
 */
interface CampaignContextValue {
    /**
     * Gets or creates a campaign manager for the given configuration.
     *
     * @template TActionId - The action ID type for this campaign
     * @param config - Campaign configuration
     * @returns Campaign manager instance
     */
    getManager<TActionId extends string | number>(
        config: CampaignConfig<TActionId>
    ): CampaignStateManager<TActionId>;
}

/**
 * React Context for campaign manager factory.
 * Use via useCampaignManagerFactory() hook.
 */
const CampaignContext = createContext<CampaignContextValue | null>(null);

/**
 * Props for CampaignProvider component.
 */
interface CampaignProviderProps {
    /**
     * Child components that can access campaign managers.
     */
    children: ReactNode;

    /**
     * Optional custom factory function for creating managers.
     * Defaults to using CampaignManagerFactory.getManager.
     * Useful for testing with mock managers.
     */
    factory?: <TActionId extends string | number>(
        config: CampaignConfig<TActionId>
    ) => CampaignStateManager<TActionId>;
}

/**
 * Provider component that supplies campaign manager factory to child components.
 * Should wrap the app or component tree that needs campaign functionality.
 *
 * @example
 * // In app root or layout
 * <CampaignProvider>
 *     <App />
 * </CampaignProvider>
 *
 * @example
 * // With custom factory for testing
 * <CampaignProvider factory={mockFactory}>
 *     <TestComponent />
 * </CampaignProvider>
 */
export const CampaignProvider = ({
    children,
    factory
}: CampaignProviderProps) => {
    const value: CampaignContextValue = useMemo(
        () => ({
            getManager: factory || ((config) => CampaignManagerFactory.getManager(config))
        }),
        [factory]
    );

    return (
        <CampaignContext.Provider value={value}>
            {children}
        </CampaignContext.Provider>
    );
};

/**
 * Hook to access campaign manager factory from context.
 * Must be used within a CampaignProvider.
 *
 * @returns Campaign manager factory
 * @throws Error if used outside CampaignProvider
 *
 * @example
 * const factory = useCampaignManagerFactory();
 * const manager = factory.getManager(myConfig);
 */
export function useCampaignManagerFactory(): CampaignContextValue {
    const context = useContext(CampaignContext);

    if (!context) {
        throw new Error(
            'useCampaignManagerFactory must be used within a CampaignProvider. ' +
            'Wrap your component tree with <CampaignProvider>.'
        );
    }

    return context;
}

/**
 * Hook to get a campaign manager for a specific configuration.
 * Returns the singleton manager instance for the given componentId.
 * Must be used within a CampaignProvider.
 *
 * @template TActionId - The action ID type for this campaign
 * @param config - Campaign configuration defining actions, mapping, and completion logic
 * @returns Campaign manager instance for this campaign
 *
 * @example
 * const config: CampaignConfig<ActionId> = {
 *     componentId: 'trial-countdown',
 *     requiredActions: [ActionId.Action1, ActionId.Action2],
 *     actionMapper: (id) => mapActionId(id)
 * };
 *
 * const LftSidePaneTrialCountdownCard = () => {
 *     const campaignManager = useCampaignManager(config);
 *
 *     const handleActionClick = (actionId: ActionId) => {
 *         campaignManager.completeAction(actionId);
 *     };
 *
 *     // ... rest of component
 * };
 */
export function useCampaignManager<TActionId extends string | number>(
    config: CampaignConfig<TActionId>
): CampaignStateManager<TActionId> {
    const factory = useCampaignManagerFactory();

    return factory.getManager(config);
}

/**
 * Hook to check if a CampaignProvider is available in the component tree.
 * Useful for conditionally rendering campaign-dependent UI.
 *
 * @returns true if CampaignProvider is available
 *
 * @example
 * const hasCampaignSupport = useCampaignProviderAvailable();
 *
 * if (!hasCampaignSupport) {
 *     return <div>Campaign features not available</div>;
 * }
 */
export function useCampaignProviderAvailable(): boolean {
    const context = useContext(CampaignContext);
    return context !== null;
}
